(() => {
  'use strict';

  const STORAGE_KEY = 'senlanseed:wishlist:v1';
  const VERSION = 1;
  const CHANGE_EVENT = 'senlan:wishlist-change';
  let toastTimer;
  let observerTimer;

  const icon = '<svg class="senlan-wishlist-toggle__icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M12 2.9l2.8 5.7 6.3.9-4.6 4.5 1.1 6.3-5.6-3-5.6 3 1.1-6.3-4.6-4.5 6.3-.9L12 2.9z"/></svg>';

  function safeGet(key) {
    try { return window.localStorage.getItem(key); } catch (_error) { return null; }
  }

  function safeSet(key, value) {
    try { window.localStorage.setItem(key, value); return true; } catch (_error) { return false; }
  }

  function normalizeHandle(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  function normalizeState(value) {
    const seen = new Set();
    const items = Array.isArray(value && value.items) ? value.items : [];
    const normalized = [];
    items.forEach((item) => {
      const handle = normalizeHandle(item && item.handle);
      if (!handle || seen.has(handle)) return;
      seen.add(handle);
      normalized.push({ handle, addedAt: typeof item.addedAt === 'string' ? item.addedAt : new Date(0).toISOString() });
    });
    return { version: VERSION, items: normalized };
  }

  function readWishlist() {
    const raw = safeGet(STORAGE_KEY);
    if (!raw) return { version: VERSION, items: [] };
    try {
      const parsed = JSON.parse(raw);
      const normalized = normalizeState(parsed);
      if (JSON.stringify(parsed) !== JSON.stringify(normalized)) safeSet(STORAGE_KEY, JSON.stringify(normalized));
      return normalized;
    } catch (_error) {
      safeSet(STORAGE_KEY, JSON.stringify({ version: VERSION, items: [] }));
      return { version: VERSION, items: [] };
    }
  }

  function emitChange(state) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: state }));
  }

  function writeWishlist(state) {
    const normalized = normalizeState(state);
    safeSet(STORAGE_KEY, JSON.stringify(normalized));
    emitChange(normalized);
    return normalized;
  }

  function hasHandle(handle, state = readWishlist()) {
    return state.items.some((item) => item.handle === normalizeHandle(handle));
  }

  function setToggleState(button, active) {
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const title = active ? 'Remove from wishlist' : 'Add to wishlist';
    button.setAttribute('aria-label', title);
    button.setAttribute('title', title);
  }

  function updateHeaderCount(state = readWishlist()) {
    const count = state.items.length;
    document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
      node.textContent = count > 99 ? '99+' : String(count);
      node.hidden = count === 0;
      node.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    });
  }

  function clearBadgeCollisions(scope = document) {
    scope.querySelectorAll('.senlan-wishlist-card-toggle').forEach((button) => {
      button.classList.remove('senlan-wishlist-card-toggle--below-badges');
      button.style.removeProperty('--senlan-wishlist-badge-clearance');
      const figure = button.closest('.product-card-image');
      if (!figure) return;
      const buttonRect = button.getBoundingClientRect();
      let bottom = 0;
      figure.querySelectorAll('.product-card--badges > *').forEach((badge) => {
        const rect = badge.getBoundingClientRect();
        const horizontalOverlap = rect.left < buttonRect.right && rect.right > buttonRect.left;
        const verticalOverlap = rect.top < buttonRect.bottom && rect.bottom > buttonRect.top;
        if (horizontalOverlap && verticalOverlap) bottom = Math.max(bottom, rect.bottom - figure.getBoundingClientRect().top);
      });
      if (bottom) {
        button.style.setProperty('--senlan-wishlist-badge-clearance', `${Math.ceil(bottom + 8)}px`);
        button.classList.add('senlan-wishlist-card-toggle--below-badges');
      }
    });
  }

  function refreshControls(scope = document, state = readWishlist()) {
    scope.querySelectorAll('[data-wishlist-toggle][data-wishlist-handle]').forEach((button) => {
      setToggleState(button, hasHandle(button.dataset.wishlistHandle, state));
    });
    updateHeaderCount(state);
    clearBadgeCollisions(scope);
  }

  function ensureToast() {
    let toast = document.querySelector('.senlan-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'senlan-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    return toast;
  }

  function showToast(message) {
    const toast = ensureToast();
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
  }

  function toggleHandle(handle) {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return;
    const state = readWishlist();
    const index = state.items.findIndex((item) => item.handle === normalizedHandle);
    if (index >= 0) {
      state.items.splice(index, 1);
      writeWishlist(state);
      showToast('Removed from wishlist');
    } else {
      state.items.push({ handle: normalizedHandle, addedAt: new Date().toISOString() });
      writeWishlist(state);
      showToast('Added to wishlist');
    }
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
  }

  function formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const format = (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || '${{amount}}';
    return format.replace(/\{\{\s*amount(?:_with_comma_separator|_no_decimals|_no_decimals_with_comma_separator)?\s*\}\}/, amount);
  }

  function productImage(product) {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.src) return product.featured_image.src;
    if (Array.isArray(product.images) && product.images[0]) return product.images[0];
    return '';
  }

  function availableVariants(product) {
    return (product.variants || []).filter((variant) => variant && variant.available);
  }

  function productCard(product) {
    const available = availableVariants(product);
    const minPrice = available.length ? Math.min(...available.map((variant) => Number(variant.price || 0))) : null;
    const image = productImage(product);
    const firstAvailableId = available.length ? String(available[0].id) : '';
    const options = (product.variants || []).map((variant) => `<option value="${escapeHtml(variant.id)}" ${variant.available ? '' : 'disabled'} ${String(variant.id) === firstAvailableId ? 'selected' : ''}>${escapeHtml(variant.title || 'Default title')}${variant.available ? '' : ' - Sold out'}</option>`).join('');
    const unavailable = available.length === 0;
    return `<article class="senlan-wishlist-v1__card" data-wishlist-product="${escapeHtml(product.handle)}">
      <a class="senlan-wishlist-v1__image-link" href="${escapeHtml(product.url || `/products/${product.handle}`)}" aria-label="View ${escapeHtml(product.title)}">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" width="720" height="720" loading="lazy">` : ''}</a>
      <div class="senlan-wishlist-v1__card-body">
        <h2 class="senlan-wishlist-v1__card-title"><a href="${escapeHtml(product.url || `/products/${product.handle}`)}">${escapeHtml(product.title)}</a></h2>
        <p class="senlan-wishlist-v1__price">${unavailable ? 'Unavailable' : `From ${escapeHtml(formatMoney(minPrice))}`}</p>
        ${product.variants && product.variants.length > 1 ? `<label class="senlan-wishlist-v1__variant-label" for="wishlist-variant-${escapeHtml(product.id)}">Choose an option</label><select id="wishlist-variant-${escapeHtml(product.id)}" class="senlan-wishlist-v1__variant-select" data-wishlist-variant>${options}</select>` : ''}
        <div class="senlan-wishlist-v1__actions"><button type="button" class="senlan-wishlist-v1__add" data-wishlist-add data-product-handle="${escapeHtml(product.handle)}" data-variant-id="${escapeHtml(firstAvailableId)}" ${unavailable ? 'disabled aria-disabled="true"' : ''}>Add to cart</button><button type="button" class="senlan-wishlist-v1__remove" data-wishlist-remove data-wishlist-handle="${escapeHtml(product.handle)}">Remove</button></div>
      </div>
    </article>`;
  }

  function unavailableCard(handle) {
    return `<article class="senlan-wishlist-v1__unavailable" data-wishlist-unavailable="${escapeHtml(handle)}"><h2>Product unavailable</h2><p>This saved product is no longer available.</p><button type="button" class="senlan-wishlist-v1__remove" data-wishlist-remove data-wishlist-handle="${escapeHtml(handle)}">Remove</button></article>`;
  }

  function emptyState() {
    return '<div class="senlan-wishlist-v1__empty"><h2>Your wishlist is empty</h2><p>Save products you would like to revisit later.</p><a class="senlan-wishlist-v1__continue" href="/collections/flower">Continue shopping</a></div>';
  }

  async function fetchProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
    return response.json();
  }

  async function mapWithConcurrency(items, limit, mapper) {
    const results = new Array(items.length);
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await mapper(items[index], index);
      }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
  }

  async function renderWishlistPage() {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;
    const content = root.querySelector('[data-wishlist-content]');
    if (!content) return;
    const state = readWishlist();
    if (!state.items.length) {
      content.innerHTML = emptyState();
      return;
    }
    content.setAttribute('aria-busy', 'true');
    const loaded = await mapWithConcurrency(state.items, 4, async (item) => {
      try { return { handle: item.handle, product: await fetchProduct(item.handle) }; }
      catch (_error) { return { handle: item.handle, product: null }; }
    });
    content.removeAttribute('aria-busy');
    const cards = loaded.map(({ handle, product }) => product ? productCard(product) : unavailableCard(handle)).join('');
    content.innerHTML = `<div class="senlan-wishlist-v1__grid">${cards}</div>`;
  }

  function resolveSectionTarget(root, selector) {
    if (!root || !selector) return null;

    if (
      root.nodeType === Node.ELEMENT_NODE &&
      typeof root.matches === 'function' &&
      root.matches(selector)
    ) {
      return root;
    }

    return typeof root.querySelector === 'function'
      ? root.querySelector(selector)
      : null;
  }

  function responseSectionTarget(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return resolveSectionTarget(parsed, selector);
  }

  function syncCartDrawerState(currentTarget, incomingTarget) {
    const wasActive = currentTarget.classList.contains('active');
    currentTarget.className = incomingTarget.className;
    if (wasActive) currentTarget.classList.add('active');

    ['role', 'aria-modal', 'aria-labelledby', 'tabindex'].forEach((name) => {
      if (incomingTarget.hasAttribute(name)) {
        currentTarget.setAttribute(name, incomingTarget.getAttribute(name));
      }
    });

    [...incomingTarget.attributes]
      .filter((attribute) => attribute.name.startsWith('data-'))
      .forEach((attribute) => currentTarget.setAttribute(attribute.name, attribute.value));
  }

  function applyCartSections(sections) {
    const targets = [
      { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
    ];
    const result = { cartDrawerUpdated: false, missingTargets: [] };

    targets.forEach(({ id, section, selector }) => {
      if (!sections || !sections[section]) {
        if (id === 'Cart-Drawer') {
          result.missingTargets.push({ id, section, selector, reason: 'section_html_missing' });
          console.warn('[Senlan Wishlist] Cart section refresh payload missing', { id, section, selector });
        }
        return;
      }

      const current = document.getElementById(id);
      const incoming = responseSectionTarget(sections[section], selector);
      const currentTarget = selector ? resolveSectionTarget(current, selector) : current;
      const incomingTarget = selector ? resolveSectionTarget(incoming, selector) : incoming;

      if (!currentTarget || !incomingTarget) {
        const missing = {
          id,
          section,
          selector,
          currentTargetFound: Boolean(currentTarget),
          incomingTargetFound: Boolean(incomingTarget),
        };
        result.missingTargets.push(missing);
        console.warn('[Senlan Wishlist] Cart section refresh target missing', missing);
        return;
      }

      if (id === 'Cart-Drawer' && selector === '.cart-drawer') {
        syncCartDrawerState(currentTarget, incomingTarget);
        result.cartDrawerUpdated = true;
      }
      currentTarget.innerHTML = incomingTarget.innerHTML;
    });

    return result;
  }

  async function addToCart(button) {
    const card = button.closest('[data-wishlist-product]');
    if (!card || button.disabled) return;
    const select = card.querySelector('[data-wishlist-variant]');
    const variantId = select ? select.value : (() => {
      const first = card.querySelector('[data-variant-id]');
      return first && first.dataset.variantId;
    })();
    if (!variantId) {
      try {
        const product = await fetchProduct(card.dataset.wishlistProduct);
        const firstAvailable = availableVariants(product)[0];
        if (!firstAvailable) throw new Error('Unavailable');
        await sendCartAdd(firstAvailable.id, button);
      } catch (_error) { showToast('This product is currently unavailable'); }
      return;
    }
    await sendCartAdd(variantId, button);
  }

  async function sendCartAdd(variantId, button) {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';
    try {
      const data = new FormData();
      data.append('id', variantId);
      data.append('quantity', '1');
      data.append('sections', 'main-cart,cart-drawer,cart-bubble');
      data.append('sections_url', window.location.pathname);
      const response = await fetch((window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js', { method: 'POST', headers: { Accept: 'application/javascript' }, body: data });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      const result = await response.json();
      const sectionUpdate = applyCartSections(result.sections);
      const drawer = document.getElementById('Cart-Drawer');
      if (!sectionUpdate.cartDrawerUpdated || !drawer || typeof drawer.open !== 'function') {
        console.warn('[Senlan Wishlist] Product was added, but the Cart Drawer was not refreshed; leaving the existing Drawer closed.', {
          cartDrawerUpdated: sectionUpdate.cartDrawerUpdated,
          drawerAvailable: Boolean(drawer),
          drawerOpenAvailable: Boolean(drawer && typeof drawer.open === 'function'),
          missingTargets: sectionUpdate.missingTargets,
        });
        showToast('Added to cart. Open your cart to view it.');
        return;
      }
      drawer.open();
      showToast('Added to cart');
    } catch (_error) {
      showToast('Unable to add this product to cart');
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  function delegatedClick(event) {
    const toggle = event.target.closest('[data-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleHandle(toggle.dataset.wishlistHandle);
      return;
    }
    const remove = event.target.closest('[data-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      const state = readWishlist();
      state.items = state.items.filter((item) => item.handle !== normalizeHandle(remove.dataset.wishlistHandle));
      writeWishlist(state);
      showToast('Removed from wishlist');
      renderWishlistPage();
      return;
    }
    const add = event.target.closest('[data-wishlist-add]');
    if (add) {
      event.preventDefault();
      addToCart(add);
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(() => refreshControls(), 40);
  }

  function init() {
    refreshControls();
    renderWishlistPage();
    document.addEventListener('click', delegatedClick);
    document.addEventListener(CHANGE_EVENT, (event) => {
      refreshControls(document, event.detail || readWishlist());
      renderWishlistPage();
    });
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) {
        const state = readWishlist();
        refreshControls(document, state);
        renderWishlistPage();
      }
    });
    window.addEventListener('resize', scheduleRefresh, { passive: true });
    new MutationObserver(scheduleRefresh).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
